import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TextBlockContent, PageSectionProps } from '@/types/cms';
import { cn } from '@/lib/utils';
import { BookOpen } from 'lucide-react';

interface TextBlockSectionProps extends PageSectionProps {
  content: TextBlockContent;
}

const TextBlockSection: React.FC<TextBlockSectionProps> = ({ 
  section, 
  content, 
  className,
  isPreview = false
}) => {
  const {
    content: textContent = '',
    alignment = 'left',
    max_width = 800,
    show_card = true,
    card_style = 'default',
    container_padding = 'default'
  } = content as TextBlockContent;

  const alignmentClasses = cn({
    "text-left": alignment === 'left',
    "text-center": alignment === 'center',
    "text-right": alignment === 'right',
  });

  const containerClasses = cn(
    "mx-auto",
    {
      "max-w-2xl": max_width <= 600,
      "max-w-4xl": max_width > 600 && max_width <= 800,
      "max-w-6xl": max_width > 800,
    }
  );

  const paddingClasses = cn({
    "py-16 px-4": container_padding === 'default',
    "py-8 px-4": container_padding === 'compact',
    "py-24 px-6": container_padding === 'spacious',
  });

  const cardClasses = cn({
    "bg-gradient-to-br from-white to-gray-50/30 border shadow-sm hover:shadow-md transition-all duration-300": card_style === 'default',
    "bg-white border-0 shadow-none": card_style === 'minimal',
    "bg-white border-2 border-gray-200 shadow-none": card_style === 'bordered',
  });

  // Simple markdown-like text processing
  const processText = (text: string) => {
    return text
      .split('\n\n')
      .map((paragraph, index) => {
        if (!paragraph.trim()) return null;
        
        // Process basic formatting
        let processedParagraph = paragraph
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold
          .replace(/\*(.*?)\*/g, '<em>$1</em>') // Italic
          .replace(/`(.*?)`/g, '<code class="px-2 py-1 bg-gray-100 rounded text-sm">$1</code>') // Code
          .replace(/\n/g, '<br />'); // Line breaks

        return (
          <p 
            key={index}
            className="mb-6 text-lg leading-relaxed text-muted-foreground last:mb-0"
            dangerouslySetInnerHTML={{ __html: processedParagraph }}
          />
        );
      })
      .filter(Boolean);
  };

  const isShortContent = textContent.length < 100;

  // Content rendering component
  const renderContent = () => {
    if (!textContent) {
      return (
        <div className="text-center py-16 space-y-4">
          <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
            <BookOpen className="w-8 h-8 text-primary" />
          </div>
          <div className="space-y-2">
            <p className="text-lg font-medium text-gray-900">No content available</p>
            <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
              This section will display rich text content once configured through the admin panel.
            </p>
          </div>
        </div>
      );
    }

    if (isShortContent) {
      return (
        <div className="text-center py-8 space-y-4">
          <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
            <BookOpen className="w-8 h-8 text-primary" />
          </div>
          <div className="space-y-2">
            <div className="prose prose-base max-w-none text-center">
              {processText(textContent)}
            </div>
            <p className="text-xs text-muted-foreground italic">
              Content preview - more details available soon
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className={cn("prose prose-base max-w-none", alignmentClasses)}>
        <div className="text-gray-900 leading-relaxed">
          {processText(textContent)}
        </div>
      </div>
    );
  };

  // Card wrapper component
  if (show_card) {
    return (
      <section className={cn(paddingClasses, className)}>
        <div className="max-w-6xl mx-auto">
          <div className={containerClasses}>
            <Card className={cardClasses}>
              {section.title && (
                <CardHeader className="pb-3">
                  <CardTitle className="text-2xl font-semibold text-gray-900 leading-tight flex items-center gap-2">
                    <BookOpen className="w-6 h-6 text-primary" />
                    {section.title}
                  </CardTitle>
                </CardHeader>
              )}
              
              <CardContent className={cn(section.title ? "pt-0" : "pt-6")}>
                {renderContent()}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    );
  }

  // Flat layout without card
  return (
    <section className={cn(paddingClasses, className)}>
      <div className="max-w-6xl mx-auto">
        <div className={containerClasses}>
          {section.title && (
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 leading-tight flex items-center gap-2">
                <BookOpen className="w-6 h-6 text-primary" />
                {section.title}
              </h2>
            </div>
          )}
          
          {renderContent()}
        </div>
      </div>
    </section>
  );
};

export default TextBlockSection;